import { Box, Flex, FormHelperText, Input, InputGroup, InputLeftAddon, Switch } from '@chakra-ui/react'
import { Dispatch, SetStateAction } from 'react'

import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { FormSelect, SearchableSelect } from '../../../../components/Select'
import VideoFileInput from '../../../../components/VideoFileInput/VideoFileInput'
import { slugify, WEBSITE_URL } from '../../../../helpers'
import { ISelectCourse } from '../../../../services/CoursesService/interfaces'
import { EventComplete, EventFormat, EventType } from '../../../../services/Event/types'
import { IEventForm } from './eventFormShared'

interface IProps {
  form: IEventForm
  setForm: Dispatch<SetStateAction<IEventForm>>
  fieldErrors: Record<string, string[]>
  saving: boolean
  event?: EventComplete
  eventTypes: EventType[]
  courses: ISelectCourse[]
  slugOwnedBySeries: boolean
  coursesOwnedBySeries: boolean
}

export function GeneralTab({
  form,
  setForm,
  fieldErrors,
  saving,
  event,
  eventTypes,
  courses,
  slugOwnedBySeries,
  coursesOwnedBySeries
}: IProps) {
  return (
    <Flex direction="column" gap={5}>
      <FormField label="Nome do Evento" htmlFor="name" error={fieldErrors['name']?.[0]} isRequired>
        <Input
          id="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          disabled={saving}
        />
      </FormField>

      <FormField label="URL do Evento" htmlFor="slug" error={fieldErrors['slug']?.[0]} isRequired>
        <InputGroup>
          <InputLeftAddon>{`${WEBSITE_URL}/eventos/`}</InputLeftAddon>
          <Input
            id="slug"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
            // Read-only instead of disabled: the URL stays focusable, selectable
            // and readable by screen readers even when the series owns it. The greyed
            // background keeps the "you can't type here" cue Chakra drops.
            isReadOnly={slugOwnedBySeries}
            bg={slugOwnedBySeries ? 'gray.50' : undefined}
            disabled={saving}
            maxLength={255}
            placeholder="meu-evento-2026"
          />
        </InputGroup>
        {slugOwnedBySeries && (
          <FormHelperText>
            URL gerada automaticamente pela série. Para personalizá-la, ative a tag &quot;URL&quot; nesta edição no
            formulário da série.
          </FormHelperText>
        )}
      </FormField>

      <Flex gap={3}>
        <FormField label="Formato" htmlFor="format" error={fieldErrors['format']?.[0]} isRequired flex="1">
          <FormSelect
            id="format"
            isDisabled={saving}
            value={form.format}
            onChange={(value) => setForm({ ...form, format: value as EventFormat })}
            options={[
              { value: 'IN_PERSON', label: 'Presencial' },
              { value: 'ONLINE', label: 'Online' },
              { value: 'HYBRID', label: 'Híbrido' }
            ]}
            placeholder="Selecione"
          />
        </FormField>

        <Flex flex="1" gap={6} alignItems="flex-end" pb={2}>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormField label="Visível no site" htmlFor="visible" mb={0}>
              <Switch
                id="visible"
                isChecked={form.visible}
                onChange={(e) => setForm({ ...form, visible: e.target.checked })}
                isDisabled={saving}
              />
            </FormField>
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            <FormField label="Check-in NFC" htmlFor="use_nfc" mb={0}>
              <Switch
                id="use_nfc"
                isChecked={form.use_nfc}
                onChange={(e) => setForm({ ...form, use_nfc: e.target.checked })}
                isDisabled={saving}
              />
            </FormField>
          </Box>
        </Flex>
      </Flex>

      <FormField label="Tipo do Evento" error={fieldErrors['types']?.[0]} isRequired>
        <SearchableSelect<number>
          isMulti
          options={eventTypes.map((type) => ({ value: type.id, label: type.name }))}
          value={form.types}
          onChange={(types) => setForm({ ...form, types })}
          placeholder="Selecione os tipos"
          entity="tipo"
          isDisabled={saving}
        />
      </FormField>

      <FormField
        label="Cursos relacionados"
        error={fieldErrors['course_ids']}
        helperText={
          coursesOwnedBySeries
            ? 'Definido pela série: todas as edições usam os mesmos cursos. Para alterar, edite a série.'
            : 'O evento aparecerá no carrossel "Gostou desse curso?" das páginas dos cursos selecionados.'
        }
      >
        <SearchableSelect<number>
          isMulti
          options={courses.map((course) => ({ value: course.id, label: course.name }))}
          value={form.course_ids}
          onChange={(courseIds) => setForm({ ...form, course_ids: courseIds })}
          placeholder="Selecione os cursos"
          entity="curso"
          isDisabled={saving || coursesOwnedBySeries}
        />
      </FormField>

      <ImageCropInput
        label="Thumb Padrão"
        value={form.thumb ?? event?.thumb ?? null}
        onChange={(file) => setForm((prev) => ({ ...prev, thumb: file }))}
        aspectRatio={375 / 221}
        suggestedSize="375 x 221 px"
        previewAspectRatio="375 / 221"
        maxSizeMB={5}
        isDisabled={saving}
        error={fieldErrors['thumb']}
      />

      <ImageCropInput
        label="Thumb Wide"
        value={form.thumb_wide ?? event?.thumb_wide ?? null}
        onChange={(file) => setForm((prev) => ({ ...prev, thumb_wide: file }))}
        aspectRatio={787 / 222}
        suggestedSize="787 x 222 px"
        previewAspectRatio="787 / 222"
        maxSizeMB={5}
        isDisabled={saving}
        error={fieldErrors['thumb_wide']}
      />

      <VideoFileInput
        label="Vídeo para página do evento (opcional)"
        file={form.video}
        existingUrl={form.remove_video ? null : form.video_url}
        onChange={(file) => setForm({ ...form, video: file, remove_video: false })}
        onRemoveExisting={() => setForm({ ...form, video: null, remove_video: true })}
        isDisabled={saving}
        maxSizeMB={100}
        helperText="Envie um arquivo MP4 (até 100MB). Quando enviado, o vídeo substitui a Thumb Wide na página do evento no site. Deixe em branco para manter a imagem."
        error={fieldErrors['video']}
      />

      <Flex gap={3} wrap="wrap">
        <FormField
          label="Data do Evento"
          htmlFor="date_start"
          error={fieldErrors['date_start']?.[0]}
          isRequired
          flex="1"
          minW="150px"
        >
          <Input
            type="date"
            id="date_start"
            value={form.date_start}
            onChange={(e) => setForm({ ...form, date_start: e.target.value })}
            disabled={saving}
          />
        </FormField>

        <Box flex="1" minW="150px" display="flex" alignItems="flex-end" pb={2} gap={3}>
          <FormField label="Mais de um dia?" mb={0}>
            <Switch
              isChecked={form.is_multiday}
              onChange={(e) => setForm({ ...form, is_multiday: e.target.checked, date_end: '' })}
              isDisabled={saving}
            />
          </FormField>
        </Box>

        {form.is_multiday && (
          <FormField
            label="Data de Término"
            htmlFor="date_end"
            error={fieldErrors['date_end']?.[0]}
            isRequired
            flex="1"
            minW="150px"
          >
            <Input
              type="date"
              id="date_end"
              value={form.date_end}
              min={form.date_start}
              onChange={(e) => setForm({ ...form, date_end: e.target.value })}
              disabled={saving}
            />
          </FormField>
        )}
      </Flex>

      <Flex gap={3}>
        <FormField
          label="Horário de Início"
          htmlFor="time_start"
          error={fieldErrors['time_start']?.[0]}
          isRequired
          flex="1"
        >
          <Input
            type="time"
            id="time_start"
            value={form.time_start}
            onChange={(e) => setForm({ ...form, time_start: e.target.value })}
            disabled={saving}
          />
        </FormField>
        <FormField
          label="Horário de Término"
          htmlFor="time_end"
          error={fieldErrors['time_end']?.[0]}
          isRequired
          flex="1"
        >
          <Input
            type="time"
            id="time_end"
            value={form.time_end}
            onChange={(e) => setForm({ ...form, time_end: e.target.value })}
            disabled={saving}
          />
        </FormField>
      </Flex>
    </Flex>
  )
}
